import React, { useEffect, useMemo, useState, useRef } from 'react';

// 가격/재고 설정 모달 (UX 스캐폴딩 확장)
// - 캘린더/리스트/일괄등록 3가지 탭 제공
// - 일단 로컬 상태만 구현, 저장 핸들러(onSave) 훅만 제공
// - ProductSettingsModal과 동일한 사이드 패널 UX/애니메이션을 재사용

const tabs = [
  { key: 'calendar', label: '캘린더' },
  { key: 'list', label: '리스트' },
  { key: 'bulk', label: '일괄등록' },
];

function startOfMonth(d) {
  const n = new Date(d.getFullYear(), d.getMonth(), 1);
  n.setHours(0, 0, 0, 0);
  return n;
}

function endOfMonth(d) {
  const n = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  n.setHours(0, 0, 0, 0);
  return n;
}

function formatYMD(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// --- UI helpers: number formatting & margin ---
function onlyDigits(s) {
  return String(s ?? '').replace(/[^0-9]/g, '');
}

function formatKRW(s) {
  const v = onlyDigits(s);
  if (!v) return '';
  return v.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function parseNumber(s) {
  const v = onlyDigits(s);
  return v ? Number(v) : 0;
}

function calcMargin(price, cost) {
  const p = parseNumber(price);
  const c = parseNumber(cost);
  if (p <= 0) return { amount: 0, rate: 0 };
  const amount = Math.max(p - c, 0);
  const rate = p > 0 ? Math.round((amount / p) * 100) : 0;
  return { amount, rate };
}

const DayCell = ({ date, value, onChange, disabled }) => {
  const [pa, setPa] = useState(value?.price?.adult ?? '');
  const [pc, setPc] = useState(value?.price?.child ?? '');
  const [pi, setPi] = useState(value?.price?.infant ?? '');
  const [ca, setCa] = useState(value?.cost?.adult ?? '');
  const [cc, setCc] = useState(value?.cost?.child ?? '');
  const [ci, setCi] = useState(value?.cost?.infant ?? '');
  const [stock, setStock] = useState(value?.stock ?? '');

  useEffect(() => {
    setPa(value?.price?.adult ?? '');
    setPc(value?.price?.child ?? '');
    setPi(value?.price?.infant ?? '');
    setCa(value?.cost?.adult ?? '');
    setCc(value?.cost?.child ?? '');
    setCi(value?.cost?.infant ?? '');
    setStock(value?.stock ?? '');
  }, [value?.price?.adult, value?.price?.child, value?.price?.infant, value?.cost?.adult, value?.cost?.child, value?.cost?.infant, value?.stock]);

  const handleBlur = () => {
    onChange && onChange({
      price: { adult: onlyDigits(pa), child: onlyDigits(pc), infant: onlyDigits(pi) },
      cost: { adult: onlyDigits(ca), child: onlyDigits(cc), infant: onlyDigits(ci) },
      stock: onlyDigits(stock),
    });
    // beautify current inputs with thousand separators after change
    setPa(formatKRW(pa));
    setPc(formatKRW(pc));
    setPi(formatKRW(pi));
    setCa(formatKRW(ca));
    setCc(formatKRW(cc));
    setCi(formatKRW(ci));
    setStock(onlyDigits(stock));
  };

  const marginA = calcMargin(pa, ca);
  const isToday = (() => {
    const now = new Date();
    return now.getFullYear() === date.getFullYear() && now.getMonth() === date.getMonth() && now.getDate() === date.getDate();
  })();

  return (
    <div className={`h-44 border rounded p-2 flex flex-col ${disabled ? 'bg-gray-50 opacity-60' : 'bg-white'}`}>
      <div className={`text-xs mb-1 flex items-center justify-between ${isToday ? 'text-blue-600 font-semibold' : 'text-gray-600'}`}>
        <span>{date.getDate()}</span>
        {isToday && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 border border-blue-100">오늘</span>}
      </div>

      {/* 판매가 */}
      <div className="mb-1">
        <div className="text-[10px] text-gray-500 mb-0.5">판매가</div>
        <div className="grid grid-cols-3 gap-1">
          <div className="relative">
            <input aria-label="판매가(성인)" type="text" inputMode="numeric" placeholder="성인" className="w-full pr-6 pl-1 py-1 border rounded text-[10px]" disabled={disabled} value={pa} onChange={(e)=>setPa(onlyDigits(e.target.value))} onBlur={handleBlur} />
            <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">원</span>
          </div>
          <div className="relative">
            <input aria-label="판매가(소아)" type="text" inputMode="numeric" placeholder="소아" className="w-full pr-6 pl-1 py-1 border rounded text-[10px]" disabled={disabled} value={pc} onChange={(e)=>setPc(onlyDigits(e.target.value))} onBlur={handleBlur} />
            <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">원</span>
          </div>
          <div className="relative">
            <input aria-label="판매가(유아)" type="text" inputMode="numeric" placeholder="유아" className="w-full pr-6 pl-1 py-1 border rounded text-[10px]" disabled={disabled} value={pi} onChange={(e)=>setPi(onlyDigits(e.target.value))} onBlur={handleBlur} />
            <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">원</span>
          </div>
        </div>
      </div>

      {/* 원가 */}
      <div className="mb-1">
        <div className="text-[10px] text-gray-500 mb-0.5">원가</div>
        <div className="grid grid-cols-3 gap-1">
          <div className="relative">
            <input aria-label="원가(성인)" type="text" inputMode="numeric" placeholder="성인" className="w-full pr-6 pl-1 py-1 border rounded text-[10px]" disabled={disabled} value={ca} onChange={(e)=>setCa(onlyDigits(e.target.value))} onBlur={handleBlur} />
            <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">원</span>
          </div>
          <div className="relative">
            <input aria-label="원가(소아)" type="text" inputMode="numeric" placeholder="소아" className="w-full pr-6 pl-1 py-1 border rounded text-[10px]" disabled={disabled} value={cc} onChange={(e)=>setCc(onlyDigits(e.target.value))} onBlur={handleBlur} />
            <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">원</span>
          </div>
          <div className="relative">
            <input aria-label="원가(유아)" type="text" inputMode="numeric" placeholder="유아" className="w-full pr-6 pl-1 py-1 border rounded text-[10px]" disabled={disabled} value={ci} onChange={(e)=>setCi(onlyDigits(e.target.value))} onBlur={handleBlur} />
            <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">원</span>
          </div>
        </div>
      </div>

      {/* 마진 */}
      <div className="text-[10px] text-gray-500 mb-1">
        마진(성인): <span className={`${marginA.amount > 0 ? 'text-green-600' : 'text-gray-400'} font-medium`}>{formatKRW(String(marginA.amount))}{marginA.amount ? '원' : ''}</span>
        {marginA.amount ? <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-green-50 text-green-700 border border-green-100">{marginA.rate}%</span> : null}
      </div>

      {/* 재고 */}
      <div className="relative mt-auto">
        <input
          type="text"
          aria-label="재고"
          inputMode="numeric"
          placeholder="재고"
          className="w-full pr-6 pl-2 py-1 border rounded text-xs"
          disabled={disabled}
          value={stock}
          onChange={(e) => setStock(onlyDigits(e.target.value))}
          onBlur={handleBlur}
        />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">명</span>
      </div>
    </div>
  );
};

const PriceStockModal = ({ product, open, onClose, onSave }) => {
  const [activeTab, setActiveTab] = useState('calendar');
  const [mountedVisible, setMountedVisible] = useState(false);
  const [animatingOut, setAnimatingOut] = useState(false);
  const backdropMouseDownRef = useRef(false);

  // 로컬 상태: date -> { price:{adult,child,infant}, cost:{adult,child,infant}, stock }
  const [data, setData] = useState({});

  // 현재 월
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));

  useEffect(() => {
    if (open) {
      setAnimatingOut(false);
      const id = requestAnimationFrame(() => setMountedVisible(true));
      return () => cancelAnimationFrame(id);
    }
  }, [open]);

  const requestClose = () => {
    setAnimatingOut(true);
    setMountedVisible(false);
    window.setTimeout(() => {
      onClose && onClose();
    }, 220);
  };

  useEffect(() => {
    // 초기 로딩 시 제품 기본값에서 seed (필요 시 서버 데이터로 대체 가능)
    if (!product) return;
    // 예시: product.prices 또는 product.inventory 같은 구조가 있다고 가정
    // 여기서는 빈 상태 유지
  }, [product]);

  const days = useMemo(() => {
    const start = startOfMonth(cursor);
    const end = endOfMonth(cursor);

    const leading = start.getDay(); // 0:일
    const total = end.getDate();

    const result = [];
    // prev month placeholders
    for (let i = 0; i < leading; i++) result.push(null);
    // current days
    for (let d = 1; d <= total; d++) {
      result.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    }
    // trailing to fill 6 rows (42 cells)
    while (result.length % 7 !== 0) result.push(null);
    while (result.length < 42) result.push(null);
    return result;
  }, [cursor]);

  const setDayValue = (d, next) => {
    if (!d) return;
    const key = formatYMD(d);
    setData(prev => ({
      ...prev,
      [key]: {
        price: {
          adult: next?.price?.adult || '',
          child: next?.price?.child || '',
          infant: next?.price?.infant || '',
        },
        cost: {
          adult: next?.cost?.adult || '',
          child: next?.cost?.child || '',
          infant: next?.cost?.infant || '',
        },
        stock: next?.stock || '',
      }
    }));
  };

  const handleSave = () => {
    // onSave로 상위 전달 (API 연동은 상위에서)
    onSave && onSave({ productNo: product?.상품번호, data });
    requestClose();
  };

  if (!open) return null;

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-200 ${animatingOut ? 'ease-in opacity-0' : 'ease-out'} ${mountedVisible && !animatingOut ? 'opacity-100' : 'opacity-0'}`}
      onMouseDown={(e) => {
        backdropMouseDownRef.current = (e.target === e.currentTarget);
      }}
      onMouseUp={(e) => {
        if (backdropMouseDownRef.current && e.target === e.currentTarget) {
          requestClose();
        }
        backdropMouseDownRef.current = false;
      }}
    >
      <div
        className={`fixed top-0 right-0 h-full w-[44rem] max-w-full bg-white shadow-lg z-50 p-6 overflow-y-auto transform transition-transform duration-200 ${animatingOut ? 'ease-in translate-x-full' : 'ease-out'} ${mountedVisible && !animatingOut ? 'translate-x-0' : 'translate-x-full'}`}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white pb-3 mb-4 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">가격/재고 설정</h2>
            <div className="flex items-center gap-2">
              <button
                className="text-sm px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50"
                onClick={requestClose}
              >
                닫기
              </button>
              <button
                className="text-sm px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700"
                onClick={handleSave}
              >
                저장
              </button>
            </div>
          </div>
          <div className="text-xs text-gray-500 mt-1">{product?.상품명} (상품번호: {product?.상품번호})</div>

          {/* Tabs */}
          <div className="flex w-full items-center rounded-lg border border-gray-200 bg-gray-50 p-1 mt-3" role="tablist">
            {tabs.map(t => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={activeTab === t.key}
                onClick={() => setActiveTab(t.key)}
                className={`flex-1 text-center px-3 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${activeTab === t.key ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        {activeTab === 'calendar' && (
          <div>
            {/* Month Navigator */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <button
                  className="px-2 py-1 rounded border border-gray-300 hover:bg-gray-50"
                  onClick={() => setCursor(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
                  aria-label="이전달"
                >
                  ◀
                </button>
                <button
                  className="px-2 py-1 rounded border border-gray-300 hover:bg-gray-50"
                  onClick={() => setCursor(startOfMonth(new Date()))}
                >
                  오늘
                </button>
                <button
                  className="px-2 py-1 rounded border border-gray-300 hover:bg-gray-50"
                  onClick={() => setCursor(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
                  aria-label="다음달"
                >
                  ▶
                </button>
              </div>
              <div className="text-sm font-medium text-gray-700">
                {cursor.getFullYear()}년 {String(cursor.getMonth() + 1)}월
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 text-xs"
                  onClick={() => setData({})}
                >
                  초기화
                </button>
              </div>
            </div>

            {/* Week headers */}
            <div className="grid grid-cols-7 gap-2 text-center text-xs text-gray-500 mb-2">
              {['일','월','화','수','목','금','토'].map((w) => (
                <div key={w} className="py-1">{w}</div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-2">
              {days.map((d, idx) => {
                const key = d ? formatYMD(d) : `empty-${idx}`;
                const value = d ? data[key] : undefined;
                const disabled = !d || d.getMonth() !== cursor.getMonth();
                return d ? (
                  <DayCell key={key} date={d} value={value} disabled={disabled} onChange={(next) => setDayValue(d, next)} />
                ) : (
                  <div key={key} className="h-28" />
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'list' && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm text-gray-700">날짜별 가격/재고 목록</div>
              <button
                className="px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 text-xs"
                onClick={() => setData({})}
              >
                초기화
              </button>
            </div>

            <div className="border rounded overflow-hidden">
              <div className="grid grid-cols-16 bg-gray-50 text-[11px] font-semibold text-gray-600">
                <div className="col-span-2 px-3 py-2 border-r">날짜</div>
                <div className="col-span-3 px-3 py-2 border-r">판매가(성/소/유)</div>
                <div className="col-span-3 px-3 py-2 border-r">원가(성/소/유)</div>
                <div className="col-span-2 px-3 py-2">재고</div>
              </div>
              <div className="max-h-[60vh] overflow-y-auto text-sm">
                {Object.keys(data).length === 0 && (
                  <div className="px-3 py-4 text-gray-400 text-sm">설정된 항목이 없습니다.</div>
                )}
                {Object.entries(data)
                  .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
                  .map(([date, val]) => (
                  <div key={date} className="grid grid-cols-16 border-t last:border-b text-[12px]">
                    <div className="col-span-2 px-3 py-2 border-r font-mono">{date}</div>
                    <div className="col-span-3 px-3 py-2 border-r">
                      <span className="inline-flex items-center gap-1">
                        <span className="px-1 rounded bg-blue-50 text-blue-700">{val?.price?.adult ? `${formatKRW(val.price.adult)}원` : '-'}</span>
                        <span className="px-1 rounded bg-blue-50 text-blue-700">{val?.price?.child ? `${formatKRW(val.price.child)}원` : '-'}</span>
                        <span className="px-1 rounded bg-blue-50 text-blue-700">{val?.price?.infant ? `${formatKRW(val.price.infant)}원` : '-'}</span>
                      </span>
                    </div>
                    <div className="col-span-3 px-3 py-2 border-r">
                      <span className="inline-flex items-center gap-1">
                        <span className="px-1 rounded bg-gray-100 text-gray-700">{val?.cost?.adult ? `${formatKRW(val.cost.adult)}원` : '-'}</span>
                        <span className="px-1 rounded bg-gray-100 text-gray-700">{val?.cost?.child ? `${formatKRW(val.cost.child)}원` : '-'}</span>
                        <span className="px-1 rounded bg-gray-100 text-gray-700">{val?.cost?.infant ? `${formatKRW(val.cost.infant)}원` : '-'}</span>
                      </span>
                    </div>
                    <div className="col-span-2 px-3 py-2 font-mono">{val?.stock || '-'}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'bulk' && (
        <div className="space-y-4">
          <div className="text-sm text-gray-700">기간을 지정하고 가격/재고를 일괄 설정합니다.</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">시작일</label>
              <input type="date" className="w-full border rounded px-3 py-2" id="bulk-start" />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">종료일</label>
              <input type="date" className="w-full border rounded px-3 py-2" id="bulk-end" />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3">
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-xs text-gray-600 mb-1">판매가(성인)</label>
                <input type="text" inputMode="numeric" className="w-full border rounded px-3 py-2" id="bulk-pa" placeholder="예: 120000" />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">판매가(소아)</label>
                <input type="text" inputMode="numeric" className="w-full border rounded px-3 py-2" id="bulk-pc" placeholder="예: 90000" />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">판매가(유아)</label>
                <input type="text" inputMode="numeric" className="w-full border rounded px-3 py-2" id="bulk-pi" placeholder="예: 30000" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-xs text-gray-600 mb-1">원가(성인)</label>
                <input type="text" inputMode="numeric" className="w-full border rounded px-3 py-2" id="bulk-ca" placeholder="예: 100000" />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">원가(소아)</label>
                <input type="text" inputMode="numeric" className="w-full border rounded px-3 py-2" id="bulk-cc" placeholder="예: 70000" />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">원가(유아)</label>
                <input type="text" inputMode="numeric" className="w-full border rounded px-3 py-2" id="bulk-ci" placeholder="예: 20000" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-xs text-gray-600 mb-1">재고(총)</label>
                <input type="text" inputMode="numeric" className="w-full border rounded px-3 py-2" id="bulk-stock" placeholder="예: 20" />
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
);
};

export default PriceStockModal;
